// src/App.tsx

import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import AuthProvider and LoginPage
import { AuthProvider } from "@/components/auth/AuthProvider";
import LoginPage from "@/components/auth/LoginPage";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

// Redu
import { Provider as ReduxProvider } from "react-redux";
import { store } from "@/store";

// Import auth animations CSS
import "@/styles/auth-animations.css";
import "./styles/funding-flash-animations.css";

// Components that are always needed (keep these as regular imports)
import { Layout, ErrorBoundary } from "@/components";
// import { LoadingSpinner } from "@/components/common";

// Loaders (keep as regular imports since they're needed for route configuration)
import { onChainPageDashboardLoader } from "@/loaders/dashboards";

import { QUERY } from "@/constants/app";

// Lazy load all page components
const ETFDashboard = lazy(() => import("./pages/ETFDashboard"));
const IndicesDashboard = lazy(() => import("./pages/IndicesDashboard"));
const InvestorsDashboard = lazy(() => import("./pages/InvestorsPage"));
const EconomicDataDashboard = lazy(
  () => import("./pages/EconomicDataDashboard")
);
const SectorComparisonPage = lazy(() => import("./pages/SectorComparisonPage"));
const SectorsOverviewPage = lazy(() => import("./pages/SectorsOverviewPage"));
const SectorDetailPage = lazy(() => import("./pages/SectorDetailPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const OnChainCexTokensPage = lazy(() => import("./pages/OnChainCexTokensPage"));
const OnChainCoreInfrastructurePage = lazy(
  () => import("./pages/OnChainCoreInfrastructurePage")
);
const OnChainDeFiPage = lazy(() => import("./pages/OnChainDeFiPage"));
const OnChainEmergingPage = lazy(() => import("./pages/OnChainEmergingPage"));
const OnChainAssetsPage = lazy(() => import("./pages/OnChainAssetsPage"));
const OnChainInfrastructurePage = lazy(
  () => import("./pages/OnChainInfrastructurePage")
);
const OnChainStablecoinsPage = lazy(
  () => import("./pages/OnChainStablecoinsPage")
);
const OnChainBitcoinEcosystemPage = lazy(
  () => import("./pages/OnChainBitcoinEcosystemPage")
);
const OnChainMiscellaneousPage = lazy(
  () => import("./pages/OnChainMiscellaneousPage")
);
const LandingPage = lazy(() => import("./pages/LandingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const YoutubeInsightsPage = lazy(() => import("./pages/youtube-insights"));
const TwitterInsightsPage = lazy(() => import("./pages/twitter-insights"));
const NewsPage = lazy(() => import("./pages/news"));
const TokenDashboardV2 = lazy(() => import("./pages/TokenDashboardV2"));
const TokensScreener = lazy(() => import("./pages/TokensScreener"));
const AssistantPage = lazy(() => import("./pages/AssistantPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const OnboardingFormPage = lazy(() => import("./pages/OnboardingFormPage"));

// Sector dashboard pages removed
const AdvisoryPage = lazy(() => import("./pages/AdvisoryPage"));

const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const PortfolioOverviewPage = lazy(
  () => import("./pages/portfolio/PortfolioPage")
);
const FredDashboard = lazy(() => import("./pages/FredDashboard"));
const OperationCenterPage = lazy(
  () => import("./pages/OperationCenterPage")
);
const SheetsPage = lazy(() => import("./pages/SheetsPage"));
// Commented out - will be activated later just like the profile page
// const AboutUs = lazy(() => import("./pages/AboutUs"));

// We rely on a global top progress bar instead of in-content fallbacks to avoid flicker
const PageLoader = () => null;

// Wrap lazy components with Suspense and ProtectedRoute
const withProtectedSuspense = (
  Component: React.LazyExoticComponent<React.FC>
): JSX.Element => {
  return (
    <ProtectedRoute>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </ProtectedRoute>
  );
};

// Wrap lazy components with just Suspense (for unprotected routes)
const withSuspense = (
  Component: React.LazyExoticComponent<React.FC>
): JSX.Element => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY.STALE_TIME,
      gcTime: QUERY.GC_TIME,
      retry: QUERY.RETRY_COUNT,
      refetchOnWindowFocus: QUERY.REFETCH_ON_WINDOW_FOCUS,
      refetchInterval: QUERY.REFETCH_INTERVAL,
      networkMode: "online",
    },
    mutations: {
      retry: 1,
    },
  },
});

const router = createBrowserRouter([
  // Public routes (no authentication required)
  {
    path: "/",
    element: withSuspense(LandingPage),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/onboarding",
    element: withSuspense(OnboardingPage),
    errorElement: <ErrorBoundary />,
  },
  // Commented out - will be activated later just like the profile page
  // {
  //   path: "/about",
  //   element: withSuspense(AboutUs),
  //   errorElement: <ErrorBoundary />,
  // },

  // Protected app routes (authentication required)
  {
    path: "/app",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      // Redirect index to /app/home
      {
        index: true,
        element: <Navigate to="/app/home" replace />,
      },
      // Home page
      {
        path: "home",
        element: withProtectedSuspense(HomePage),
      },
      {
        path: "portfolio/overview",
        element: withProtectedSuspense(PortfolioOverviewPage),
      },
      {
        path: "portfolio/metrics",
        element: withProtectedSuspense(PortfolioOverviewPage),
      },
      {
        path: "portfolio/alerts",
        element: withProtectedSuspense(PortfolioOverviewPage),
      },
      {
        path: "profile",
        element: withProtectedSuspense(UserProfilePage),
      },
      {
        path: "onboarding-form",
        element: withProtectedSuspense(OnboardingFormPage),
      },
      {
        path: "coming-soon",
        element: withProtectedSuspense(ComingSoon),
      },
      {
        path: "trade",
        element: withProtectedSuspense(ComingSoon),
      },
      // Redirect old market route to home
      { path: "market", element: <Navigate to="/app/home" replace /> },
      // Sectors routes - Phase 1 reorganization
      {
        path: "sectors",
        element: withProtectedSuspense(SectorsOverviewPage),
      },
      {
        path: "sectors/compare",
        element: withProtectedSuspense(SectorComparisonPage),
        loader: (args) =>
          onChainPageDashboardLoader({
            ...args,
            context: { queryClient },
          }),
      },
      // Sector dashboard routes (formerly on-chain) - must be before :sectorSlug catch-all
      {
        path: "sectors/overall",
        element: withProtectedSuspense(SectorComparisonPage),
        loader: (args) =>
          onChainPageDashboardLoader({
            ...args,
            context: { queryClient },
          }),
      },
      {
        path: "sectors/cex-tokens",
        element: withProtectedSuspense(OnChainCexTokensPage),
      },
      {
        path: "sectors/core-infrastructure",
        element: withProtectedSuspense(OnChainCoreInfrastructurePage),
      },
      {
        path: "sectors/core-infrastructure/:subTabId",
        element: withProtectedSuspense(OnChainCoreInfrastructurePage),
      },
      {
        path: "sectors/defi",
        element: withProtectedSuspense(OnChainDeFiPage),
      },
      {
        path: "sectors/defi/:subTabId",
        element: withProtectedSuspense(OnChainDeFiPage),
      },
      {
        path: "sectors/defi/:subTabId/:subSubTabId",
        element: withProtectedSuspense(OnChainDeFiPage),
      },
      {
        path: "sectors/emerging",
        element: withProtectedSuspense(OnChainEmergingPage),
      },
      {
        path: "sectors/emerging/:subTabId",
        element: withProtectedSuspense(OnChainEmergingPage),
      },
      {
        path: "sectors/emerging/:subTabId/:subSubTabId",
        element: withProtectedSuspense(OnChainEmergingPage),
      },
      {
        path: "sectors/assets",
        element: withProtectedSuspense(OnChainAssetsPage),
      },
      {
        path: "sectors/stablecoins",
        element: withProtectedSuspense(OnChainStablecoinsPage),
      },
      {
        path: "sectors/stablecoins/:subTabId",
        element: withProtectedSuspense(OnChainStablecoinsPage),
      },
      {
        path: "sectors/assets/:subTabId",
        element: withProtectedSuspense(OnChainAssetsPage),
      },
      {
        path: "sectors/infrastructure",
        element: withProtectedSuspense(OnChainInfrastructurePage),
      },
      {
        path: "sectors/infrastructure/:subTabId",
        element: withProtectedSuspense(OnChainInfrastructurePage),
      },
      {
        path: "sectors/bitcoin-ecosystem",
        element: withProtectedSuspense(OnChainBitcoinEcosystemPage),
      },
      {
        path: "sectors/bitcoin-ecosystem/:subTabId",
        element: withProtectedSuspense(OnChainBitcoinEcosystemPage),
      },
      {
        path: "sectors/miscellaneous",
        element: withProtectedSuspense(OnChainMiscellaneousPage),
      },
      {
        path: "sectors/miscellaneous/:subTabId",
        element: withProtectedSuspense(OnChainMiscellaneousPage),
      },
      // Generic sector detail page - catch-all for other sectors
      {
        path: "sectors/:sectorSlug",
        element: withProtectedSuspense(SectorDetailPage),
      },
      {
        path: "tokens",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/charts",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/charts/:chartType",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/movers",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/compare",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/watchlists",
        element: withProtectedSuspense(TokensScreener),
      },
      {
        path: "tokens/alerts",
        element: withProtectedSuspense(TokensScreener),
      },

      // Non Dashboard Pages
      {
        path: "advisory",
        element: withProtectedSuspense(AdvisoryPage),
      },
      {
        path: "bridges",
        element: withProtectedSuspense(NotFound),
      },
      {
        path: "sentiment",
        element: withProtectedSuspense(ComingSoon),
      },
      {
        path: "market-sentiment",
        element: withProtectedSuspense(ComingSoon),
      },
      // ============================================
      // Phase 2: Social & News routes (new structure)
      // ============================================
      {
        path: "social/twitter",
        element: withProtectedSuspense(TwitterInsightsPage),
      },
      {
        path: "social/youtube",
        element: withProtectedSuspense(YoutubeInsightsPage),
      },
      {
        path: "social/news",
        element: withProtectedSuspense(NewsPage),
      },
      // Redirects from old social routes (backwards compatibility)
      {
        path: "twitter-insights",
        element: <Navigate to="/app/social/twitter" replace />,
      },
      {
        path: "youtube-insights",
        element: <Navigate to="/app/social/youtube" replace />,
      },
      {
        path: "multimedia-insights",
        element: <Navigate to="/app/social/youtube" replace />,
      },
      {
        path: "news",
        element: <Navigate to="/app/social/news" replace />,
      },

      // ============================================
      // Phase 3: Macro & Indicators routes (new names)
      // ============================================
      {
        path: "macro",
        element: withProtectedSuspense(EconomicDataDashboard),
      },
      {
        path: "macro/:tabId",
        element: withProtectedSuspense(EconomicDataDashboard),
      },
      {
        path: "indicators",
        element: withProtectedSuspense(IndicesDashboard),
      },
      // Redirects from old routes (backwards compatibility)
      {
        path: "economic-data",
        element: <Navigate to="/app/macro" replace />,
      },
      {
        path: "indices",
        element: <Navigate to="/app/indicators" replace />,
      },

      // ============================================
      // Other routes
      // ============================================
      {
        path: "etfs",
        element: withProtectedSuspense(ETFDashboard),
      },
      {
        path: "investors",
        element: withProtectedSuspense(InvestorsDashboard),
      },
      {
        path: "macro/economy-charts",
        element: withProtectedSuspense(FredDashboard),
      },
      {
        path: "assistant",
        element: withProtectedSuspense(AssistantPage),
      },
      {
        path: "workspace/dashboards",
        element: withProtectedSuspense(OperationCenterPage),
      },
      {
        path: "workspace/sheets",
        element: withProtectedSuspense(SheetsPage),
      },
      {
        path: "workspace/sheets/:sheetId",
        element: withProtectedSuspense(SheetsPage),
      },
      // Redirects from old routes (backwards compatibility)
      {
        path: "workspace",
        element: <Navigate to="/app/workspace/dashboards" replace />,
      },
      {
        path: "sheets",
        element: <Navigate to="/app/workspace/sheets" replace />,
      },
      {
        path: "sheets/:sheetId",
        element: <Navigate to="/app/workspace/sheets" replace />,
      },
      {
        path: "admin",
        element: withProtectedSuspense(AdminDashboard),
      },
      // Commented out - will be activated later just like the profile page
      // {
      //   path: "about-us",
      //   element: withProtectedSuspense(AboutUs),
      // },
      // Token routes - unified token page
      {
        path: "tokens/:tokenId",
        element: withProtectedSuspense(TokenDashboardV2),
      },
      // Catch-all for undefined routes - show NotFound page
      {
        path: "*",
        element: withProtectedSuspense(NotFound),
      },
    ],
  },

  // Catch-all for any other routes
  {
    path: "*",
    element: withSuspense(LandingPage),
  },
]);

const App: React.FC = () => {
  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </ReduxProvider>
  );
};

export default App;
